import Button from '@atoms/Button/Button';
import Cta from '@atoms/Cta/Cta';
import Image from '@atoms/Image/Image';
import Text from '@atoms/Text/Text';
import { HeroVariantEnum, IMediaHero } from '@models/IHero';
import { ILink } from '@models/ILink';
import SiteEnum from '@models/enums/SiteEnum';
import ThemeEnum from '@models/enums/ThemeEnum';
import VariantTypeEnum from '@models/enums/VariantTypeEnum';
import { HeroMedia } from '@molecules/HeroMedia/HeroMedia';
import { VideoButton } from '@molecules/VideoButton/VideoButton';
import backgroundColours from '@utility/config/backgroundColours';
import classNames from 'classnames';
import ReactPlayer from 'react-player';

const MediaHero = (props: IMediaHero) => {
  const {
    backgroundColour,
    ctaOne,
    ctaStyle,
    ctaTwo,
    fontColour = ThemeEnum.Light,
    hasQBrickPlayButton,
    image,
    site,
    subText,
    title,
    trumpetImage,
    trumpetText,
    variant,
    video,
    deactivateAutoplay = false,
  } = props;
  if (!title && !video && !image) return null;

  const bgHexCode = backgroundColour?.startsWith('#')
    ? backgroundColour
    : backgroundColour && backgroundColours?.[backgroundColour];

  const { Full, FullLarge, Split, SplitReverse, Stacked } = HeroVariantEnum;
  const isFullVariant = variant === Full;
  const isFullLargeVariant = variant === FullLarge;
  const isSplitVariant = variant === Split;
  const isSplitReverseVariant = variant === SplitReverse;
  const isStackedVariant = variant === Stacked;

  const isFullOrFullLargeVariant = [Full, FullLarge].includes(variant);
  const isSplitOrReverseVariant = [Split, SplitReverse].includes(variant);

  const hasMedia =
    image?.url ||
    (video?.videoSource ? ReactPlayer.canPlay(video.videoSource) : false);

  const HeroButton = ({
    link,
    secondary,
  }: {
    link: ILink;
    secondary?: boolean;
  }) => {
    const { text, url, target } = link;
    switch (ctaStyle) {
      case VariantTypeEnum.Cta:
        return <Cta site={site} {...link} />;
      default:
        return (
          <Button
            site={site}
            as="link"
            key={text}
            href={url}
            target={target}
            label={text}
            modifier={secondary ? 'outline' : 'fill'}
            variant={site === SiteEnum.B2B ? 'yellow' : 'white'}
            buttonClasses={secondary ? 'tw-text-white' : ''}
          />
        );
    }
  };

  const qbrickButton =
    hasQBrickPlayButton && video ? (
      <VideoButton
        darkFont={fontColour === ThemeEnum.Dark}
        videoSource={video.videoSource}
        {...props}
      />
    ) : null;

  return (
    <section
      className={classNames(
        'media-hero',
        /* 
          For accessibility purposes only, we will never see this background as it's covered
          By the media, but flags that the text doesn't contrast.
        */
        isFullOrFullLargeVariant &&
          hasMedia &&
          (fontColour === ThemeEnum.Light ? 'tw-bg-black' : 'tw-bg-white')
      )}
      style={{ backgroundColor: bgHexCode }}
    >
      <div
        className={classNames('tw-relative tw-flex', {
          'tw-min-h-[480px] md:tw-min-h-[608px] xl:tw-min-h-[75vh]':
            isFullOrFullLargeVariant,
          'tw-flex-col xl:tw-flex-row-reverse': isSplitVariant,
          'tw-flex-col xl:tw-flex-row': isSplitReverseVariant,
          'tw-flex-col': isStackedVariant,
        })}
      >
        {hasMedia && isFullOrFullLargeVariant && (
          <div className="tw-absolute tw-inset-0 tw-z-10 tw-bg-black/50" />
        )}

        {hasMedia ? (
          <HeroMedia
            className={classNames(
              isFullOrFullLargeVariant && 'tw-absolute tw-inset-0',
              isSplitOrReverseVariant &&
                'tw-relative  tw-h-[468px] xl:tw-h-[75vh] xl:tw-min-h-[608px] xl:tw-basis-1/2',
              isStackedVariant &&
                'tw-relative tw-h-[468px] md:tw-h-[608px] xl:tw-h-[75vh]'
            )}
            darkFont={fontColour === ThemeEnum.Dark}
            isSplit={isSplitOrReverseVariant}
            autoPlay={!deactivateAutoplay}
            {...props}
            hasQBrickPlayButton={hasQBrickPlayButton && variant !== Full}
          />
        ) : null}
        <div
          className={classNames(
            'tw-container tw-relative tw-z-20 tw-flex tw-py-10 md:tw-py-16',
            {
              'xl:tw-basis-1/2': isSplitOrReverseVariant,
            }
          )}
        >
          <div
            className={classNames(
              'tw-m-auto tw-text-center',
              isFullLargeVariant ? 'tw-max-w-[924px]' : 'tw-max-w-[640px]',
              fontColour === ThemeEnum.Dark
                ? 'tw-text-grey-100'
                : 'tw-text-white'
            )}
          >
            {trumpetText ? (
              <Text variant="body" className="tw-mb-2 tw-uppercase md:tw-mb-4">
                {trumpetText}
              </Text>
            ) : null}
            {trumpetImage ? (
              <div className="tw-mb-2 tw-min-h-7 md:tw-mb-4">
                <Image
                  image={trumpetImage}
                  imageClasses={classNames(
                    'tw-mx-auto',
                    // if aspect ratio is greater than or equal to 6 the remove width limitations on image
                    trumpetImage.originalWidth / trumpetImage.originalHeight >=
                      6
                      ? 'tw-max-h-7 md:tw-max-h-9'
                      : 'tw-max-h-12 tw-max-w-32 md:tw-max-h-16 md:tw-max-w-44'
                  )}
                />
              </div>
            ) : null}
            <div
              className={classNames(
                isFullLargeVariant ? 'xl:tw-gap-2xl' : '',
                'tw-flex tw-flex-col tw-gap-xxs md:tw-gap-lg'
              )}
            >
              {title ? (
                <Text
                  variant={
                    isFullLargeVariant ? 'hero-headline-large' : 'hero-headline'
                  }
                  as="h1"
                >
                  {title}
                </Text>
              ) : null}
              {subText ? (
                <>
                  <Text
                    variant="body"
                    className={isFullOrFullLargeVariant ? 'md:tw-hidden' : ''}
                  >
                    {subText}
                  </Text>
                  {isFullOrFullLargeVariant ? (
                    <Text variant="body-18" className="max-md:tw-hidden">
                      {subText}
                    </Text>
                  ) : null}
                </>
              ) : null}
              {(ctaOne || ctaTwo) && (
                <div
                  className={classNames(
                    'tw-flex tw-justify-center',
                    ctaStyle === VariantTypeEnum.Cta ? 'tw-gap-8' : 'tw-gap-4',
                    fontColour === ThemeEnum.Light && 'tw-dark'
                  )}
                >
                  {ctaOne ? <HeroButton link={ctaOne} /> : null}
                  {ctaTwo ? <HeroButton link={ctaTwo} secondary /> : null}
                </div>
              )}
              {qbrickButton && isFullVariant ? qbrickButton : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MediaHero;
